import { Box, Stack, useTheme } from "@mui/material";
import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { LEVEL } from "../../consts";
import { LearningObjectAlternativeContext } from "../../context/LearningObjectAlternativeContext";
import type { ReferencedCourse, ReferencedLearningActivity } from "../../types";
import type { StructuredAlternativeSectionItem } from "../../utils/learningObjectStructure";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";
import { DotButton, DotColumn } from "../CourseActivityCard/ActivityDotNavigation.styles";
import { CourseActivityCard } from "../CourseActivityCard/CourseActivityCard.component";
import { ActivitySelectedProvider } from "../DetailsEsploraRestyle/IndexActivitySelected.context";
import { handleTeachersAndTutors } from "../LearningActiviyModal/utils";
import { PathCourseAccordion } from "../PathCourseAccordion/PathCourseAccordion.component";

type LearningObjectChoiceListProps = {
  alternatives: StructuredAlternativeSectionItem["alternatives"];
  disabled: boolean;
  onSelect: (id: number) => void;
  selectedId: number | null;
};

export function LearningObjectChoiceList({
  alternatives, disabled, onSelect, selectedId
}: LearningObjectChoiceListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const selectionColor = theme.customColors.backgroundPrimaryCta;
  const cardsRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const { rootIdNumber } = useRouteParamsLo();
  const selectedIndex = alternatives.findIndex((item) => item.id === selectedId);

  const selectAndFocus = (index: number) => {
    if (disabled) return;
    onSelect(alternatives[index].id);
    const card = cardsRef.current?.querySelector<HTMLElement>(`[data-choice-id="${alternatives[index].id}"]`);
    dotsRef.current?.querySelectorAll<HTMLElement>("[role=\"radio\"]")[index]?.focus({ preventScroll: true });
    card?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    let nextIndex: number;
    switch (event.key) {
    case "ArrowDown":
    case "ArrowRight": nextIndex = (index + 1) % alternatives.length; break;
    case "ArrowUp":
    case "ArrowLeft": nextIndex = (index - 1 + alternatives.length) % alternatives.length; break;
    case "Home": nextIndex = 0; break;
    case "End": nextIndex = alternatives.length - 1; break;
    case " ":
    case "Enter": nextIndex = index; break;
    default: return;
    }
    event.preventDefault();
    event.stopPropagation();
    selectAndFocus(nextIndex);
  };

  return (
    <Stack alignItems="flex-start" direction="row" gap={ 0.5 } sx={ { [theme.breakpoints.up("sm")]: { gap: 1.5 } } }>
      <DotColumn
        aria-label={ t("alternative_section_choices", { defaultValue: "Contenuti alternativi" }) }
        ref={ dotsRef }
        role="radiogroup"
        sx={ { alignSelf: "flex-start", gap: "0.5rem", maxHeight: "12rem", overflowY: "auto" } }
      >
        { alternatives.map((item, index) => (
          <DotButton
            $active={ item.id === selectedId }
            $isComplete={ false }
            $isNavigationDot={ false }
            aria-label={ t("alternative_section_select_item", {
              defaultValue: "Seleziona {{title}}", title: item.title
            }) }
            aria-checked={ item.id === selectedId }
            disabled={ disabled }
            key={ item.id }
            onClick={ () => selectAndFocus(index) }
            onKeyDown={ (event) => handleKeyDown(event, index) }
            role="radio"
            sx={ { flexShrink: 0 } }
            tabIndex={ disabled ? -1 : index === Math.max(selectedIndex, 0) ? 0 : -1 }
            type="button"
          />
        )) }
      </DotColumn>
      <Stack
        gap="0.5rem"
        minWidth={ 0 }
        ref={ cardsRef }
        sx={ { flex: 1 } }
      >
        <LearningObjectAlternativeContext.Provider value={ true }>
          <ActivitySelectedProvider syncUrlOnSelection={ false }>
            { alternatives.map((item, index) => item.learningObjectTypology === LEVEL.COURSE ? (
              <Box
                data-choice-id={ item.id }
                key={ item.id }
                sx={ {
                  "& > [data-testid='path-course-accordion-root']": {
                    "&:hover, &:focus": { background: "transparent" },
                    backgroundColor: "transparent",
                    ...(item.id === selectedId && { borderColor: selectionColor })
                  }
                } }
              >
                <PathCourseAccordion
                  course={ item as ReferencedCourse }
                  index={ index }
                  isAlternative
                  rootId={ Number.isFinite(rootIdNumber) ? rootIdNumber : undefined }
                />
              </Box>
            ) : (
              <Box
                aria-disabled={ disabled }
                aria-label={ item.title }
                aria-pressed={ item.id === selectedId }
                data-choice-id={ item.id }
                key={ item.id }
                onClick={ () => { if (!disabled) onSelect(item.id); } }
                onKeyDown={ (event) => handleKeyDown(event, index) }
                role="button"
                sx={ {
                  "& .card-bottom-box": { display: "none" },
                  "& .course-activity-card": {
                    backgroundColor: theme.customColors.backgroundPrimaryControl,
                    borderColor: item.id === selectedId ? selectionColor : "transparent",
                    borderRadius: "0.5rem",
                    minHeight: "4rem"
                  },
                  "&:focus-visible": { outline: `2px solid ${selectionColor}`, outlineOffset: "3px" },
                  borderRadius: "0.5rem",
                  cursor: disabled ? "default" : "pointer",
                  outline: "none"
                } }
                tabIndex={ disabled ? -1 : 0 }
              >
                { /* Activity clicks choose the alternative; their learning actions stay inactive here. */ }
                <Box ref={ (element: HTMLDivElement | null) => element?.setAttribute("inert", "") }>
                  <CourseActivityCard
                    { ...item as ReferencedLearningActivity }
                    activity={ item as ReferencedLearningActivity }
                    index={ index + 1 }
                    madeAttempts={ item.madeAttempts ?? undefined }
                    teachers={ handleTeachersAndTutors(item.teachers ?? [], item.tutors ?? [], t) }
                  />
                </Box>
              </Box>
            )) }
          </ActivitySelectedProvider>
        </LearningObjectAlternativeContext.Provider>
      </Stack>
    </Stack>
  );
}
